import { useState, useEffect } from 'react';
import {
  Package,
  Plus,
  Search,
  Filter,
  Edit2,
  Trash2,
  X,
  Save,
  Droplets,
  Utensils,
  Heart,
  Home,
  Zap,
  Radio,
  Car,
  Wrench,
  Shirt,
  HelpCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Ban,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';
import {
  getAllResources,
  addResource,
  updateResource,
  deleteResource,
} from '../db/database';
import type { Resource, ResourceCategory, ResourceStatus } from '../types';

const categoryIcons: Record<ResourceCategory, typeof Package> = {
  water: Droplets,
  food: Utensils,
  medical: Heart,
  shelter: Home,
  power: Zap,
  communication: Radio,
  transportation: Car,
  tools: Wrench,
  clothing: Shirt,
  other: HelpCircle,
};

const statusConfig: Record<ResourceStatus, { icon: typeof CheckCircle; color: string; label: string }> = {
  available: { icon: CheckCircle, color: 'safe', label: 'Available' },
  requested: { icon: Clock, color: 'warning', label: 'Requested' },
  reserved: { icon: AlertTriangle, color: 'warning', label: 'Reserved' },
  depleted: { icon: Ban, color: 'emergency', label: 'Depleted' },
};

export function Resources() {
  const { addNotification } = useApp();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ResourceCategory | 'all'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    category: 'other' as ResourceCategory,
    quantity: 1,
    unit: 'units',
    status: 'available' as ResourceStatus,
    location: '',
    description: '',
    ownerName: '',
  });

  useEffect(() => {
    loadResources();
  }, []);

  async function loadResources() {
    try {
      const data = await getAllResources();
      setResources(data);
    } catch (error) {
      console.error('Failed to load resources:', error);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      name: '',
      category: 'other',
      quantity: 1,
      unit: 'units',
      status: 'available',
      location: '',
      description: '',
      ownerName: '',
    });
    setEditingResource(null);
  }

  function openAddModal() {
    resetForm();
    setShowModal(true);
  }

  function openEditModal(resource: Resource) {
    setEditingResource(resource);
    setFormData({
      name: resource.name,
      category: resource.category,
      quantity: resource.quantity,
      unit: resource.unit,
      status: resource.status,
      location: resource.location || '',
      description: resource.description || '',
      ownerName: resource.ownerName || '',
    });
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    try {
      if (editingResource) {
        await updateResource(editingResource.id, formData);
        addNotification('success', 'Updated', 'Resource updated successfully');
      } else {
        await addResource(formData);
        addNotification('success', 'Added', 'Resource added to coordination board');
      }
      setShowModal(false);
      resetForm();
      loadResources();
    } catch (error) {
      console.error('Failed to save resource:', error);
      addNotification('error', 'Error', 'Failed to save resource');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this resource?')) return;
    await deleteResource(id);
    loadResources();
  }

  async function handleStatusChange(id: string, status: ResourceStatus) {
    await updateResource(id, { status });
    loadResources();
  }

  const filteredResources = resources.filter((resource) => {
    const matchesSearch =
      resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || resource.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories: ResourceCategory[] = [
    'water',
    'food',
    'medical',
    'shelter',
    'power',
    'communication',
    'transportation',
    'tools',
    'clothing',
    'other',
  ];

  const getCategoryStats = () => {
    const stats: Record<ResourceCategory, number> = {} as Record<ResourceCategory, number>;
    categories.forEach((cat) => {
      stats[cat] = resources.filter((r) => r.category === cat && r.status === 'available').length;
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Package className="w-6 h-6 text-emergency-400" />
            Resource Coordination
          </h1>
          <p className="text-gray-400 mt-1">Share and request emergency supplies</p>
        </div>
        <button onClick={openAddModal} className="btn-emergency">
          <Plus className="w-4 h-4" />
          Add Resource
        </button>
      </div>

      {/* Category Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {categories.slice(0, 5).map((cat) => {
          const Icon = categoryIcons[cat];
          const count = categoryStats[cat];
          return (
            <button
              key={cat}
              onClick={() => setCategoryFilter(categoryFilter === cat ? 'all' : cat)}
              className={`card p-4 text-center transition-all ${
                categoryFilter === cat
                  ? 'ring-2 ring-emergency-500 bg-emergency-900/20'
                  : 'hover:bg-gray-700/50'
              }`}
            >
              <Icon className={`w-6 h-6 mx-auto mb-2 ${
                count > 0 ? 'text-safe-400' : 'text-gray-500'
              }`} />
              <p className="text-sm font-medium text-white capitalize">{cat}</p>
              <p className={`text-xs ${count > 0 ? 'text-safe-400' : 'text-gray-500'}`}>
                {count} available
              </p>
            </button>
          );
        })}
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
          <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
              categoryFilter === 'all'
                ? 'bg-emergency-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap capitalize ${
                categoryFilter === cat
                  ? 'bg-emergency-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Resources Grid */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-gray-700 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-700 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredResources.length === 0 ? (
        <div className="card p-8 text-center">
          <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No resources found</h3>
          <p className="text-gray-400 mb-4">
            {searchQuery || categoryFilter !== 'all'
              ? 'Try adjusting your search or filter'
              : 'Start sharing resources with your community'}
          </p>
          <button onClick={openAddModal} className="btn-emergency">
            <Plus className="w-4 h-4" />
            Add Resource
          </button>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {filteredResources.map((resource) => {
            const CategoryIcon = categoryIcons[resource.category];
            const statusInfo = statusConfig[resource.status];
            const StatusIcon = statusInfo.icon;

            return (
              <motion.div
                key={resource.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`card p-4 ${
                  resource.status === 'available'
                    ? 'border-l-4 border-l-safe-500'
                    : resource.status === 'depleted'
                    ? 'border-l-4 border-l-emergency-500'
                    : 'border-l-4 border-l-warning-500'
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-lg ${
                      resource.status === 'available'
                        ? 'bg-safe-900/50'
                        : resource.status === 'depleted'
                        ? 'bg-emergency-900/50'
                        : 'bg-warning-900/50'
                    }`}>
                      <CategoryIcon className={`w-5 h-5 ${
                        resource.status === 'available'
                          ? 'text-safe-400'
                          : resource.status === 'depleted'
                          ? 'text-emergency-400'
                          : 'text-warning-400'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{resource.name}</h3>
                      <p className="text-xs text-gray-400 capitalize">{resource.category}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEditModal(resource)}
                      className="p-1.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(resource.id)}
                      className="p-1.5 rounded hover:bg-emergency-900/50 text-gray-400 hover:text-emergency-400 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-white">
                      {resource.quantity}
                    </span>
                    <span className="text-gray-400">{resource.unit}</span>
                  </div>

                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${
                    statusInfo.color === 'safe'
                      ? 'bg-safe-900/50 text-safe-300'
                      : statusInfo.color === 'warning'
                      ? 'bg-warning-900/50 text-warning-300'
                      : 'bg-emergency-900/50 text-emergency-300'
                  }`}>
                    <StatusIcon className="w-4 h-4" />
                    <span className="text-sm font-medium">{statusInfo.label}</span>
                  </div>

                  {resource.description && (
                    <p className="text-sm text-gray-400">{resource.description}</p>
                  )}

                  {resource.location && (
                    <p className="text-xs text-gray-500">📍 {resource.location}</p>
                  )}

                  {resource.ownerName && (
                    <p className="text-xs text-gray-500">👤 {resource.ownerName}</p>
                  )}
                </div>

                {/* Quick Status Change */}
                <div className="mt-3 pt-3 border-t border-gray-700 flex gap-1">
                  {(['available', 'requested', 'reserved', 'depleted'] as ResourceStatus[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(resource.id, status)}
                      disabled={resource.status === status}
                      className={`flex-1 py-1 text-xs rounded transition-colors ${
                        resource.status === status
                          ? 'bg-gray-600 text-white cursor-default'
                          : 'bg-gray-700/50 text-gray-400 hover:bg-gray-600 hover:text-white'
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      )}

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  {editingResource ? 'Edit Resource' : 'Add Resource'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-700 text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="label">Resource Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="input"
                    placeholder="Bottled Water, First Aid Kit..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Category *</label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value as ResourceCategory })}
                      className="input"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat} className="capitalize">
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as ResourceStatus })}
                      className="input"
                    >
                      <option value="available">Available</option>
                      <option value="requested">Requested</option>
                      <option value="reserved">Reserved</option>
                      <option value="depleted">Depleted</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Quantity *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="label">Unit</label>
                    <input
                      type="text"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      className="input"
                      placeholder="bottles, packs, units"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="input"
                    placeholder="Where is this resource located?"
                  />
                </div>

                <div>
                  <label className="label">Owner/Contact</label>
                  <input
                    type="text"
                    value={formData.ownerName}
                    onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                    className="input"
                    placeholder="Who to contact"
                  />
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="input min-h-[80px] resize-none"
                    placeholder="Additional details..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-emergency flex-1">
                    <Save className="w-4 h-4" />
                    {editingResource ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
